import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import ScheduleCalendar from '../../components/ScheduleCalendar';
import { useCenterSchedule, CenterSchedule, ScheduleColor, SCHEDULE_COLORS, getColorStyle } from '../../contexts/CenterScheduleContext';

const CenterScheduleManagement: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formIsHoliday, setFormIsHoliday] = useState(false);
  const [formColor, setFormColor] = useState<ScheduleColor>('green');
  const [saving, setSaving] = useState(false);

  const { schedules, loading, fetchSchedulesByMonth, addSchedule, updateSchedule, deleteSchedule } = useCenterSchedule();

  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    fetchSchedulesByMonth(year, month);
  }, [currentMonth, fetchSchedulesByMonth]);

  const selectedSchedules = selectedDate
    ? schedules.filter(s => s.event_date === selectedDate)
    : [];

  const formatDateKr = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-');
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return `${y}년 ${Number(m)}월 ${Number(d)}일 (${dayNames[date.getDay()]})`;
  };

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    setIsAdding(false);
    setEditingId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormTitle('');
    setFormIsHoliday(false);
    setFormColor('green');
  };

  const handleAdd = async () => {
    if (!selectedDate || !formTitle.trim()) return;
    setSaving(true);
    try {
      await addSchedule({
        event_date: selectedDate,
        title: formTitle.trim(),
        is_holiday: formIsHoliday,
        color: formIsHoliday ? 'red' : formColor,
      });
      setIsAdding(false);
      resetForm();
    } catch (error) {
      alert('일정 추가에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (schedule: CenterSchedule) => {
    setEditingId(schedule.id);
    setFormTitle(schedule.title);
    setFormIsHoliday(schedule.is_holiday);
    setFormColor((schedule.color as ScheduleColor) || 'green');
    setIsAdding(false);
  };

  const handleUpdate = async () => {
    if (editingId === null || !formTitle.trim()) return;
    setSaving(true);
    try {
      await updateSchedule(editingId, {
        title: formTitle.trim(),
        is_holiday: formIsHoliday,
        color: formIsHoliday ? 'red' : formColor,
      });
      setEditingId(null);
      resetForm();
    } catch (error) {
      alert('일정 수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;
    try {
      await deleteSchedule(id);
    } catch (error) {
      alert('일정 삭제에 실패했습니다.');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setIsAdding(false);
    resetForm();
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">센터 일정 관리</h1>
        <p className="mt-1 text-sm text-gray-500">달력에서 날짜를 클릭하여 일정을 추가하거나 수정하세요.</p>
        <p className="mt-1 text-sm text-gray-500">
          <span className="text-red-600 font-medium">휴관일</span>로 지정한 날은 수강료 결제기한(다음 업무일 오후 2시) 계산에서 제외됩니다. 일·월요일은 지정하지 않아도 자동으로 제외됩니다.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* 달력 */}
        <div className="flex-1">
          <ScheduleCalendar
            currentMonth={currentMonth}
            onMonthChange={setCurrentMonth}
            schedules={schedules}
            loading={loading}
            onDateClick={handleDateClick}
          />
        </div>

        {/* 사이드 패널: 날짜 선택 시 일정 관리 */}
        <div className="w-full lg:w-80 xl:w-96">
          {selectedDate ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* 패널 헤더 */}
              <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b">
                <h3 className="font-semibold text-gray-800">{formatDateKr(selectedDate)}</h3>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="p-1 rounded hover:bg-gray-200 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* 기존 일정 목록 */}
              <div className="px-5 py-4 space-y-3">
                {selectedSchedules.length === 0 && !isAdding && (
                  <p className="text-sm text-gray-400 text-center py-4">등록된 일정이 없습니다.</p>
                )}

                {selectedSchedules.map(schedule => (
                  <div key={schedule.id} className="group">
                    {editingId === schedule.id ? (
                      /* 수정 모드 */
                      <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="text"
                          value={formTitle}
                          onChange={e => setFormTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="일정 내용"
                        />
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={formIsHoliday}
                            onChange={e => setFormIsHoliday(e.target.checked)}
                            className="w-4 h-4 text-red-500 rounded border-gray-300 focus:ring-red-500"
                          />
                          <span className="text-red-600 font-medium">휴관일</span>
                        </label>
                        {!formIsHoliday && (
                          <div>
                            <span className="text-xs text-gray-500 mb-1.5 block">색상</span>
                            <div className="flex gap-2">
                              {SCHEDULE_COLORS.filter(c => c.value !== 'red').map(c => (
                                <button
                                  key={c.value}
                                  type="button"
                                  onClick={() => setFormColor(c.value)}
                                  className={`w-7 h-7 rounded-full border-2 transition-all ${c.bg} ${
                                    formColor === c.value ? 'border-gray-800 scale-110' : 'border-gray-300'
                                  }`}
                                  title={c.label}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={handleUpdate}
                            disabled={saving || !formTitle.trim()}
                            className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                          >
                            {saving ? '저장 중...' : '수정'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* 표시 모드 */
                      <div className={`flex items-center justify-between p-3 rounded-lg border ${
                        schedule.is_holiday
                          ? 'bg-red-50 border-red-200'
                          : `${getColorStyle(schedule.color).bg} ${getColorStyle(schedule.color).border}`
                      }`}>
                        <div className="flex items-center gap-2 min-w-0">
                          {schedule.is_holiday && (
                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">휴관</span>
                          )}
                          <span className="text-sm text-gray-800 truncate">{schedule.title}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEdit(schedule)}
                            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                            title="수정"
                          >
                            <PencilIcon className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(schedule.id)}
                            className="p-1.5 rounded hover:bg-red-50 transition-colors"
                            title="삭제"
                          >
                            <TrashIcon className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* 추가 폼 */}
                {isAdding && (
                  <div className="space-y-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <input
                      type="text"
                      value={formTitle}
                      onChange={e => setFormTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="일정 내용을 입력하세요"
                      autoFocus
                      onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    />
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formIsHoliday}
                        onChange={e => setFormIsHoliday(e.target.checked)}
                        className="w-4 h-4 text-red-500 rounded border-gray-300 focus:ring-red-500"
                      />
                      <span className="text-red-600 font-medium">휴관일</span>
                    </label>
                    {!formIsHoliday && (
                      <div>
                        <span className="text-xs text-gray-500 mb-1.5 block">색상</span>
                        <div className="flex gap-2">
                          {SCHEDULE_COLORS.filter(c => c.value !== 'red').map(c => (
                            <button
                              key={c.value}
                              type="button"
                              onClick={() => setFormColor(c.value)}
                              className={`w-7 h-7 rounded-full border-2 transition-all ${c.bg} ${
                                formColor === c.value ? 'border-gray-800 scale-110' : 'border-gray-300'
                              }`}
                              title={c.label}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleAdd}
                        disabled={saving || !formTitle.trim()}
                        className="flex-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {saving ? '저장 중...' : '추가'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* 일정 추가 버튼 */}
              {!isAdding && editingId === null && (
                <div className="px-5 pb-4">
                  <button
                    onClick={() => { setIsAdding(true); resetForm(); }}
                    className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    일정 추가
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="text-gray-300 mb-3">
                <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
              </div>
              <p className="text-sm text-gray-400">달력에서 날짜를 클릭하면<br />일정을 관리할 수 있습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CenterScheduleManagement;
